import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography, Box, LinearProgress, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, Paper,
} from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import CancelImportButton, { CANCELLABLE_STATUSES } from "./CancelImportButton";

const useStyles = makeStyles((theme) => ({
  root: { flex: 1, minHeight: 0, padding: theme.spacing(2), display: "flex", flexDirection: "column", gap: theme.spacing(2), overflow: "auto" },
  counters: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap" },
  counter: { textAlign: "center", minWidth: 88, padding: theme.spacing(1), border: `1px solid ${theme.palette.divider}`, borderRadius: 8 },
  counterNum: { fontSize: "1.6rem", fontWeight: 700, lineHeight: 1 },
  pausedBox: { background: "#FFF4E5", border: "1px solid #FFB74D", borderRadius: 8, padding: theme.spacing(2), display: "flex", gap: theme.spacing(1), alignItems: "flex-start" },
  tableWrap: { maxHeight: 320, overflow: "auto" },
  actions: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
}));

const STATUS_COLORS = {
  processing: "#1976d2",
  completed: "#2e7d32",
  paused_meta: "#ed6c02",
  failed: "#d32f2f",
  cancelled: "#757575",
};

const rowStatusLabel = (st) => i18n.t(`bulkSend.result.rowStatus.${st}`) || st;

// backLabelKey: i18n key para el botón inferior de "volver" — permite reusar
// este componente tanto para el resultado recién enviado (Nuevo envío) como
// para el detalle de un import elegido desde el historial (Volver al historial).
const BulkImportResult = ({ importId, companyId, onBack, backLabelKey = "bulkSend.result.newImport" }) => {
  const classes = useStyles();
  const [imp, setImp] = useState(null);
  const [rows, setRows] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await api.get(`/bulk-send/imports/${importId}`);
      setImp(data.import);
      return data.import;
    } catch (e) {
      return null;
    }
  }, [importId]);

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await api.get(`/bulk-send/imports/${importId}/rows`);
      setRows(data.rows || []);
    } catch (_) { /* noop */ }
  }, [importId]);

  useEffect(() => {
    fetchDetail();
    fetchRows();
  }, [fetchDetail, fetchRows]);

  // Socket en vivo (singleton compartido, scoped por company vía el room del backend)
  useEffect(() => {
    const socket = getSocket();
    const handler = (payload) => {
      if (!payload || payload.importId !== importId) return;
      setImp((prev) => ({ ...(prev || {}), ...payload }));
      if (["completed", "paused_meta", "failed", "cancelled"].includes(payload.status)) {
        fetchRows();
      }
    };
    socket.on("bulkImport:progress", handler);
    return () => { socket.off("bulkImport:progress", handler); };
  }, [importId, fetchRows]);

  // Fallback polling mientras esté en curso (por si el socket no llega)
  useEffect(() => {
    if (!imp) return undefined;
    if (["processing", "pending"].includes(imp.status)) {
      pollRef.current = setInterval(() => { fetchDetail(); }, 5000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    return undefined;
  }, [imp, fetchDetail]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/bulk-send/imports/${importId}/retry-failed`);
      toast.success(i18n.t("bulkSend.result.retryDone", { n: data.requeued }));
      await fetchDetail();
      await fetchRows();
    } catch (e) {
      toastError(e);
    } finally {
      setRetrying(false);
    }
  };

  if (!imp) return <Box p={2}><LinearProgress /></Box>;

  const total = imp.rowsTotal ?? 0;
  const done = (imp.rowsSent ?? 0) + (imp.rowsFailed ?? 0) + (imp.rowsSkipped ?? 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const inProgress = ["processing", "pending"].includes(imp.status);
  const canRetry = ["completed", "paused_meta", "failed"].includes(imp.status) && (imp.rowsFailed ?? 0) > 0;

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" gap={8} style={{ gap: 8 }}>
        <Typography variant="h6">{i18n.t("bulkSend.result.title")}</Typography>
        <Chip
          label={i18n.t(`bulkSend.result.${imp.status === "paused_meta" ? "pausedMeta" : imp.status}`)}
          style={{ background: STATUS_COLORS[imp.status] || "#757575", color: "#fff" }}
          size="small"
        />
      </Box>

      <Box>
        <LinearProgress variant={inProgress ? "indeterminate" : "determinate"} value={pct} />
        <Typography variant="caption" color="textSecondary">{done}/{total} ({pct}%)</Typography>
      </Box>

      <div className={classes.counters}>
        {[
          ["sent", imp.rowsSent, "#2e7d32"],
          ["failedC", imp.rowsFailed, "#d32f2f"],
          ["skipped", imp.rowsSkipped, "#757575"],
          ["total", imp.rowsTotal, "#1976d2"],
        ].map(([k, v, c]) => (
          <div key={k} className={classes.counter}>
            <div className={classes.counterNum} style={{ color: c }}>{v ?? 0}</div>
            <Typography variant="caption" color="textSecondary">{i18n.t(`bulkSend.result.${k}`)}</Typography>
          </div>
        ))}
      </div>

      {imp.status === "paused_meta" && (
        <div className={classes.pausedBox}>
          <WarningIcon style={{ color: "#ed6c02" }} />
          <Typography variant="body2">{i18n.t("bulkSend.result.pausedExplain")}</Typography>
        </div>
      )}

      <div className={classes.actions}>
        {canRetry && (
          <Button variant="contained" color="primary" startIcon={<ReplayIcon />} onClick={handleRetry} disabled={retrying}>
            {retrying ? i18n.t("bulkSend.result.retrying") : i18n.t("bulkSend.result.retry")}
          </Button>
        )}
        {CANCELLABLE_STATUSES.includes(imp.status) && (
          <CancelImportButton importId={importId} onCancelled={() => fetchDetail()} />
        )}
        {!inProgress && onBack && (
          <Button variant="outlined" onClick={onBack}>{i18n.t(backLabelKey)}</Button>
        )}
      </div>

      <Paper variant="outlined" className={classes.tableWrap}>
        <Typography variant="subtitle2" style={{ padding: 8 }}>{i18n.t("bulkSend.result.rowsTitle")}</Typography>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{i18n.t("bulkSend.preview.colName")}</TableCell>
              <TableCell>{i18n.t("bulkSend.preview.colPhone")}</TableCell>
              <TableCell>{i18n.t("bulkSend.result.colStatus")}</TableCell>
              <TableCell>{i18n.t("bulkSend.result.colDetail")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.rowIndex}</TableCell>
                <TableCell>{r.normalizedData?.name || "—"}</TableCell>
                <TableCell>{r.normalizedData?.phone || "—"}</TableCell>
                <TableCell>{rowStatusLabel(r.status)}</TableCell>
                <TableCell style={{ maxWidth: 240, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {r.errorMessage || r.skipReason || (r.messageId ? "✓" : "")}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography variant="caption" color="textSecondary">—</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default BulkImportResult;
